// The composer-side read contract for what happened to a composed practice.
//
// Completion/skip state is wayfinder-owned: the write model (tile lifecycle)
// does not exist yet, and no journey table carries a completion column. The
// composer still needs the *dimension* — a retrigger's prompt should know that
// a practice was done or skipped — so the read ships as a declared interface
// with an all-`unknown` stub. The lifecycle work implements this interface
// against its real store; nothing composer-side changes when it does. Lives in
// the shared journey seam because both sides of the pathway touch it: the
// composer consumes it, the lifecycle model must satisfy it.

/** What a composed practice's outcome read can resolve to. */
export const PRACTICE_OUTCOME_STATES = ['done', 'skipped', 'unknown'] as const;
export type PracticeOutcomeState = (typeof PRACTICE_OUTCOME_STATES)[number];

export interface PracticeOutcomesReader {
  /**
   * Outcome state per composed-practice id. Total over the requested ids: an
   * id the store knows nothing about resolves `'unknown'`, never absent — so
   * renderers branch on state, not on map membership.
   */
  getOutcomes(
    memberId: string,
    practiceIds: readonly string[],
  ): Promise<ReadonlyMap<string, PracticeOutcomeState>>;
}

/**
 * The shipped default: no outcome store exists yet, so every id is
 * `'unknown'`. Renderers must degrade gracefully against this — an unknown
 * outcome renders no outcome claim at all.
 */
export const unknownOutcomesReader: PracticeOutcomesReader = {
  async getOutcomes(_memberId, practiceIds) {
    return new Map(practiceIds.map((id) => [id, 'unknown' as const]));
  },
};
